/* eslint react-hooks/exhaustive-deps: "warn" */
import { type FormEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './GovernmentRegis.module.scss';

type Message = {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  sources?: string[];
};

type QaPair = {
  keywords: string[];
  answer: string;
  sources: string[];
};

const QA: QaPair[] = [
  {
    keywords: ['own', 'owner', 'ownership', 'control'],
    answer:
      'SAMPLE INFRASTRUCTURE (UK) LTD is 100% held by SAMPLE HOLDINGS B.V. (Netherlands), 82% held by SAMPLE CAPITAL S.à r.l. (Luxembourg), 76% held by SAMPLE STATE INVESTMENT AUTHORITY, a state-owned parent in a third country. Effective control resolves to 62.32%.',
    sources: ['Companies House · GB', 'KVK Handelsregister · NL', 'RCS Luxembourg · LU'],
  },
  {
    keywords: ['sanction', 'designat', 'ofac', 'ofsi'],
    answer:
      "SAMPLE GULF TRADING FZE's ultimate owner, D. MARLOWE, appears on the OFAC consolidated list. The stake reaches SAMPLE GULF INSTRUMENTS FZE through a 55% holding.",
    sources: ['OFAC consolidated list', 'UAE company registry'],
  },
  {
    keywords: ['pep', 'politically'],
    answer: 'A. VEIGA, director of SAMPLE MERIDIAN CIVILS LTD, matches a politically exposed person record.',
    sources: ['Companies House · GB', 'PEP sources'],
  },
  {
    keywords: ['active', 'status', 'strike'],
    answer: 'SAMPLE LOWLAND UTILITIES LTD is under a compulsory strike-off action, filed via Gazette notice.',
    sources: ['Companies House · GB'],
  },
  {
    keywords: ['help', 'what can', 'example', 'question'],
    answer:
      'Ask about ownership, sanctions matches, PEP matches or company status on the sample file. Try: "Who ultimately owns it?"',
    sources: [],
  },
];

const FALLBACK =
  "That's not in the sample file. Ask about ownership, sanctions, PEP matches or company status — or book a briefing to run it on your own register.";

const SUGGESTIONS = ['Who ultimately owns it?', 'Any sanctions match?', 'Is it still active?', 'Any PEP matches?'];

const answerFor = (question: string): QaPair | undefined =>
  QA.find((qa) => qa.keywords.some((k) => question.toLowerCase().includes(k)));

let uid = 0;
const nextId = () => {
  uid += 1;
  return uid;
};

const seed = (): Message[] => [
  {
    id: nextId(),
    role: 'assistant',
    text: 'Ask about the sample file — ownership, sanctions, PEP matches or company status. Every claim carries its register.',
  },
];

export const GovernmentRegis = () => {
  const { t } = useInt();
  const [messages, setMessages] = useState<Message[]>(seed);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  const ask = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || thinking) {
      return;
    }

    setMessages((prev) => [...prev, { id: nextId(), role: 'user', text: trimmed }]);
    setInput('');
    setThinking(true);

    setTimeout(
      () => {
        const match = answerFor(trimmed);
        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: 'assistant',
            text: match ? match.answer : FALLBACK,
            sources: match?.sources,
          },
        ]);
        setThinking(false);
      },
      700 + Math.random() * 500,
    );
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    ask(input);
  };

  return (
    <section className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.grid}>
          <div className={styles.copy}>
            <TitleBlock
              title={t('Regis is a verification model that answers only from the register.')}
              theme="dark"
              align="left"
            />

            <div className={styles.ways}>
              <div>
                <span className={styles.wayK}>{t('Ask it a question')}</span>
                <p>
                  <b>{t('Plain language in, sourced answer out')}</b>{' '}
                  {t('— with the register attached to every claim.')}
                </p>
              </div>
              <div>
                <span className={styles.wayK}>{t('Or build on it')}</span>
                <p>
                  <b>{t('The same model over the API')}</b> {t('— inside your own case management or risk system.')}
                </p>
              </div>
            </div>

            <div className={styles.guard}>
              <b>{t('No answer without a source')}</b>
              <span>{t('If the record was not retrieved, Regis says so.')}</span>
            </div>

            <div className={styles.cta}>
              <Button type="white" size="xl" href="/free-trial">
                {t('Try Regis AI')}
              </Button>
              <Button type="white-outline" size="xl" href="/api">
                {t('Try Regis API')}
              </Button>
            </div>
          </div>

          <div className={styles.chatWrap}>
            <div className={styles.chat}>
              <div className={styles.chatHead}>
                <span className={styles.chatDot} />
                Regis
                <span className={styles.chatBadge}>{t('Illustrative · sample file')}</span>
              </div>

              <div className={styles.chatBody} ref={bodyRef} aria-live="polite">
                {messages.map((msg) => (
                  <div className={cn(styles.msg, styles[`msg_${msg.role}`])} key={msg.id}>
                    <span className={styles.msgText}>{msg.text}</span>
                    {!!msg.sources?.length && (
                      <div className={styles.msgSrc}>
                        {msg.sources.map((src) => (
                          <span key={src}>{src}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {thinking && (
                  <div className={styles.think}>
                    <i />
                    <i />
                    <i />
                  </div>
                )}
              </div>

              <div className={styles.chips}>
                {SUGGESTIONS.map((s) => (
                  <button type="button" key={s} onClick={() => ask(s)} disabled={thinking}>
                    {s}
                  </button>
                ))}
              </div>

              <form className={styles.chatIn} onSubmit={onSubmit}>
                <input
                  type="text"
                  autoComplete="off"
                  placeholder={t('Ask about the sample file…')}
                  aria-label="Ask Regis about the sample file"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                />
                <button type="submit" aria-label="Send" disabled={thinking || !input.trim()}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h13M12 5l7 7-7 7" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
